import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { fieldClass } from "@/lib/validation";

function validate(token, next, confirm, tokenInUrl) {
  const errs = {};
  if (!tokenInUrl && !token.trim()) errs.token   = "Reset-Token ist erforderlich.";
  if (!next)                        errs.next    = "Neues Passwort ist erforderlich.";
  else if (next.length < 10)        errs.next    = "Mindestens 10 Zeichen erforderlich.";
  if (!confirm)                     errs.confirm = "Bestätigung ist erforderlich.";
  else if (next && confirm && next !== confirm) errs.confirm = "Passwörter stimmen nicht überein.";
  return errs;
}

export default function AdminResetPassword() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const tokenInUrl = params.get("token") || "";
  const [token,     setToken]    = useState(tokenInUrl);
  const [next,      setNext]     = useState("");
  const [confirm,   setConfirm]  = useState("");
  const [touched,   setTouched]  = useState({ token: false, next: false, confirm: false });
  const [submitted, setSubmitted] = useState(false);
  const [apiError,  setApiError]  = useState(null);
  const [done,      setDone]      = useState(false);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => { setToken(tokenInUrl); }, [tokenInUrl]);

  const errs = validate(token, next, confirm, tokenInUrl);
  const show = (f) => (submitted || touched[f]) && !!errs[f];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setApiError(null);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: next });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (_e) {
      setApiError("Token ungültig oder abgelaufen. Bitte neu anfordern.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-porcelain px-5 py-12">
      <div className="w-full max-w-md bg-white border border-sandBeige rounded-3xl p-8 shadow-soft">
        <p className="font-serif text-cocoa text-2xl lowercase">
          womenintech<span className="text-raspberry">.life</span>
        </p>
        <h1 className="font-serif text-3xl text-cocoa mt-4">Neues Passwort setzen</h1>
        {!done ? (
          <form onSubmit={submit} noValidate className="mt-6 flex flex-col gap-4" data-testid="reset-form">
            {/* Token (only if not in URL) */}
            {!tokenInUrl && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="reset-token" className="text-xs font-medium text-cocoa">
                  Reset-Token <span className="text-raspberry">*</span>
                </label>
                <input
                  id="reset-token"
                  value={token}
                  onChange={(e) => { setToken(e.target.value); setApiError(null); }}
                  onBlur={() => setTouched((t) => ({ ...t, token: true }))}
                  placeholder="Token aus E-Mail"
                  className={fieldClass("wit-input", show("token"), !!token.trim() && !errs.token)}
                  data-testid="reset-token"
                />
                {show("token") && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle size={12} />{errs.token}
                  </p>
                )}
              </div>
            )}

            {/* New password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reset-new" className="text-xs font-medium text-cocoa">
                Neues Passwort <span className="text-raspberry">*</span>
              </label>
              <input
                id="reset-new"
                type="password"
                value={next}
                onChange={(e) => { setNext(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, next: true }))}
                placeholder="Mind. 10 Zeichen"
                className={fieldClass("wit-input", show("next"), !!next && !errs.next)}
                data-testid="reset-new"
              />
              {show("next") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.next}
                </p>
              )}
              {!show("next") && next && next.length < 10 && (
                <p className="text-xs text-warmGrey">{next.length}/10 Zeichen</p>
              )}
            </div>

            {/* Confirm */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reset-confirm" className="text-xs font-medium text-cocoa">
                Passwort bestätigen <span className="text-raspberry">*</span>
              </label>
              <input
                id="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                placeholder="Passwort wiederholen"
                className={fieldClass("wit-input", show("confirm"), !!confirm && !errs.confirm)}
                data-testid="reset-confirm"
              />
              {show("confirm") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.confirm}
                </p>
              )}
            </div>

            {apiError && (
              <p className="text-sm text-coral flex items-center gap-2" role="alert">
                <AlertCircle size={14} />{apiError}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="wit-btn-primary disabled:opacity-60"
              data-testid="reset-submit"
            >
              {loading ? "Speichern …" : <>Passwort speichern <ArrowRight size={16} /></>}
            </button>
            <Link to="/admin/login" className="text-xs text-warmGrey hover:text-raspberry text-center">
              Zurück zum Login
            </Link>
          </form>
        ) : (
          <div className="mt-6 flex flex-col gap-4" data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-sageMint flex items-center justify-center">
              <Check size={20} className="text-raspberry" />
            </div>
            <p className="text-sm text-cocoa">Passwort geändert. Du wirst weitergeleitet …</p>
          </div>
        )}
      </div>
    </div>
  );
}
